
import { downloadAsJPG, downloadAsPDF, createPrintDocument } from "./printingBridge";
import { Student } from "@/services/db/types";
import { toast } from "sonner";
import { generateCertificateContent, generateCertificateFilename } from "./student/certificateContentGenerator";
import { getCertificateStyles } from "./student/certificateStyles";
import { validateStudentForCertificate } from "./student/certificateUtils";

/**
 * Export student certificate as JPG - ULTRA-FAST
 */
export const printStudentCertificateAsJPG = async (
  student: Student, 
  programId?: string, 
  instructorName?: string, 
  directorName?: string,
  grade?: string,
  percentage?: string
) => {
  try {
    validateStudentForCertificate(student, programId);
    
    const loadingToast = toast.loading('Generating student certificate JPG...');

    // Enhanced console logging to debug parameter passing
    console.log('=== CERTIFICATE JPG GENERATION DEBUG ===');
    console.log('Student name:', student.name);
    console.log('Grade received:', grade);
    console.log('Percentage received:', percentage);
    console.log('Instructor name:', instructorName);
    console.log('Director name:', directorName);
    console.log('Program ID:', programId);
    console.log('========================================');

    const content = generateCertificateContent(student, programId, instructorName, directorName, grade, percentage);
    const filename = generateCertificateFilename(student);
    const styles = getCertificateStyles();
    
    // Generate the complete print document
    const printDocument = createPrintDocument(content, filename, styles);
    
    // Download as JPG with ultra-fast settings
    await downloadAsJPG(printDocument, filename, {
      onError: (error) => {
        console.error('JPG download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download student certificate');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Student certificate downloaded as JPG!');
    
  } catch (error) {
    console.error('Error downloading student certificate:', error);
    toast.error('Failed to download student certificate');
  }
};

/**
 * Export student certificate as PDF - ULTRA-FAST
 */
export const printStudentCertificateAsPDF = async (
  student: Student, 
  programId?: string, 
  instructorName?: string, 
  directorName?: string,
  grade?: string,
  percentage?: string
) => {
  try {
    validateStudentForCertificate(student, programId);
    
    const loadingToast = toast.loading('Generating student certificate PDF...');

    // Enhanced console logging to debug parameter passing
    console.log('=== CERTIFICATE PDF GENERATION DEBUG ===');
    console.log('Student name:', student.name);
    console.log('Grade received:', grade);
    console.log('Percentage received:', percentage);
    console.log('Instructor name:', instructorName);
    console.log('Director name:', directorName);
    console.log('Program ID:', programId);
    console.log('========================================');

    const content = generateCertificateContent(student, programId, instructorName, directorName, grade, percentage);
    const filename = generateCertificateFilename(student);
    const styles = getCertificateStyles();
    
    // Generate the complete print document
    const printDocument = createPrintDocument(content, filename, styles);
    
    // Download as PDF with ultra-fast settings
    await downloadAsPDF(printDocument, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download student certificate');
      }
    });
    
    toast.dismiss(loadingToast);
    toast.success('Student certificate downloaded as PDF!');
    
  } catch (error) {
    console.error('Error downloading student certificate:', error);
    toast.error('Failed to download student certificate');
  }
};

/**
 * Legacy function - now calls JPG version for backward compatibility
 */
export const printStudentCertificate = async (
  student: Student, 
  programId?: string, 
  instructorName?: string, 
  directorName?: string,
  grade?: string,
  percentage?: string
) => {
  await printStudentCertificateAsJPG(student, programId, instructorName, directorName, grade, percentage);
};
